import { AppActions, AppState, IUserData, actionTypes, AtaSettings } from '../Types';
import { find } from 'lodash';
import logger from 'Utils/Logger';

export const defaultAtaSettings: AtaSettings = {
  ataEnabled: true,
  goalTempo: 150,
  usingCustomGoalTempo: false,
};

export const defaultState: AppState = {
  errors: [],
  loading: false,
  data: [],
  unitSelect: 1,
  levelSelect: 0,
  tempo: 72,
  tutorialData: [],
  tutorialDataProcessing: false,
  userLevelData: [],
  highestLevelCompleted: 0,
  levelData: [],
  tiersByLevels: [],
  midiInput: null,
  lastShownSurvey: null,
  isMidiConnected: null,
  hasShownIntroduction: null,
  userDataFetched: false,
  userData: {
    // set some defaults so the reducer senses the change
    last_shown_survey: null,
    lesson_tutorial_seenat: null,
    repertoire_tutorial_seenat: null,
  },
  isFullScreen: false,
  repPageSelected: 'Home',
  errorRecognitionActive: { left: true, right: true },
  playOnStart: false,
  interactedWithPage: false,
  showLevelPreview: false,
  pendingDeletion: false,
  dashboardCacheInvalidated: false,
  dashboardData: null,
  zoomLevel: 0.9,
  tempoValues: {},
  ataSettings: defaultAtaSettings,
};

export const mainAppReducer = (state = defaultState, action: AppActions): AppState => {
  switch (action.type) {
    case actionTypes.CLEAR_DATA:
      logger.debug('mainAppReducer: CLEAR_DATA action received, resetting state to default');
      return { ...defaultState };
    case actionTypes.FETCH_CLIENT_DATA_REQUEST:
      return {
        ...state,
        loading: true,
      };

    case actionTypes.FETCH_CLIENT_DATA_SUCCESS:
      return {
        ...state,
        loading: false,
        data: [action.payload.data],
        errors: [],
      };

    case actionTypes.FETCH_CLIENT_DATA_ERROR:
      return {
        ...state,
        loading: false,
        errors: [action.payload.error],
      };

    case actionTypes.SET_LEVEL_SELECT:
      return {
        ...state,
        levelSelect: action.payload.levelSelect,
        currentUserLevelData: find(
          state.userLevelData,
          (uld) =>
            uld.level?.level_number === state.levelData[action.payload.levelSelect]?.level_number,
        ),
        currentLevelTiers: find(
          state.tiersByLevels,
          (tbl) => tbl.level_number === state.levelData[action.payload.levelSelect]?.level_number,
        ),
      };

    case actionTypes.SET_UNIT_SELECT:
      return {
        ...state,
        unitSelect: action.payload.unitSelect,
      };

    case actionTypes.SET_HIGHEST_LEVEL_COMPLETED:
      return {
        ...state,
        highestLevelCompleted: action.payload.highestLevelCompleted,
      };

    case actionTypes.FETCH_TUTORIAL_DATA_SUCCESS:
      return {
        ...state,
        tutorialData: action.payload.tutorialData,
      };

    case actionTypes.FETCH_TUTORIAL_DATA_PROCESSING:
      return {
        ...state,
        tutorialDataProcessing: action.payload,
      };

    case actionTypes.AUTHENTICATED_STARTUP:
      return {
        ...state,
        userDataFetched: false,
      };

    case actionTypes.FETCH_USER_LEVEL_SUCCESS:
      return {
        ...state,
        userLevelData: action.payload.userLevelData,
      };

    case actionTypes.FETCH_LEVEL_DATA_SUCCESS:
      return {
        ...state,
        levelData: action.payload.levelData,
      };

    case actionTypes.UPDATE_TIERS_BY_LEVELS:
      return {
        ...state,
        tiersByLevels: action.payload,
      };

    case actionTypes.MIDI_INPUT_SETUP:
      return {
        ...state,
        midiInput: action.payload,
      };

    case actionTypes.SET_SHOWN_INTRODUCTION:
      return {
        ...state,
        hasShownIntroduction: action.payload,
      };

    case actionTypes.SET_USER_DATA:
      return {
        ...state,
        userDataFetched: true,
        userData: {
          ...action.payload,
          // Set defaults for metronome and downbeats if undefined or null
          rep_metronome_sound: action.payload.rep_metronome_sound ?? true,
          rep_downbeats_sound: action.payload.rep_downbeats_sound ?? true,
        },
      };

    case actionTypes.UPDATE_USER_DATA:
      // this code checks for actual changes and otherwise returns state. (Prevents rerender on useEffect dependency)
      // way way more verbose because of type guard for keys
      if (state.userData) {
        logger.debug('Updating user data', action.payload);
        let changesPresent = false;
        const isKeyOfIUserData = (key: keyof IUserData): boolean => {
          if (!state.userData) return false;
          return key in state.userData;
        };
        for (const key in action.payload) {
          const keyWithType: keyof IUserData = key as keyof IUserData; // WTF typescript...
          if (
            isKeyOfIUserData(keyWithType) &&
            state.userData[keyWithType] !== action.payload[keyWithType]
          ) {
            changesPresent = true;
            break;
          }
        }

        if (!changesPresent) {
          return state;
        }
      }
      return {
        ...state,
        userData: {
          ...state.userData,
          ...action.payload,
        },
      };

    case actionTypes.LOAD_USER_STATE:
      return {
        ...state,
        ...action.payload,
      };

    case actionTypes.SET_FULL_SCREEN:
      return {
        ...state,
        isFullScreen: action.payload,
      };

    case actionTypes.SET_SUBSCRIPTION_STATUS:
      // Return state if subscription_status is not different; if returns spread of userData WILL trigger useEffect on userData
      if (!state.userData || state.userData.subscription_status == action.payload) {
        return state;
      }
      return {
        ...state,
        userData: {
          ...state.userData,
          subscription_status: action.payload,
        },
      };

    case actionTypes.SET_REP_PAGE_SELECTED:
      return {
        ...state,
        repPageSelected: action.payload,
      };

    case actionTypes.SET_ERROR_RECOGNITION_ACTIVE:
      return {
        ...state,
        errorRecognitionActive: action.payload,
      };

    case actionTypes.SET_PLAY_ON_START:
      return {
        ...state,
        playOnStart: action.payload.playOnStart,
      };

    case actionTypes.SET_INTERACTED_WITH_PAGE:
      return {
        ...state,
        interactedWithPage: action.payload,
      };

    case actionTypes.INVALIDATE_DASHBOARD_CACHE:
      return {
        ...state,
        dashboardCacheInvalidated: !state.dashboardCacheInvalidated,
        dashboardData: null, // Clear cached data when invalidating
      };

    case actionTypes.SET_DASHBOARD_DATA: {
      console.log('SET_DASHBOARD_DATA reducer called with payload:', action.payload);
      console.log('Previous state dashboardData:', state.dashboardData);
      const newState = {
        ...state,
        dashboardData: action.payload,
      };
      console.log('New state dashboardData:', newState.dashboardData);
      return newState;
    }

    case actionTypes.SET_ZOOM_LEVEL:
      return {
        ...state,
        zoomLevel: action.payload ?? state.zoomLevel,
      };

    case actionTypes.SET_TEMPO: {
      const { tempo, level, tier } = action.payload;
      const tempoValues = state?.tempoValues ?? {};
      const levelTempoValues = tempoValues[level] ?? {};
      const newTempoValues = {
        ...tempoValues,
        [level]: {
          ...levelTempoValues,
          [tier]: tempo,
        },
      };
      /*const newSettings = {
        ...settings,
        [action.payload.tier]: {
          ...(settings[action.payload.tier] ?? defaultTempoSettings),
          currentTempo: action.payload.tempo,
        },
      };*/
      return {
        ...state,
        tempoValues: newTempoValues,
      };
    }

    case actionTypes.SET_AUTO_TEMPO_ADJUSTMENT: {
      const ataSettings = {
        ...(state?.ataSettings ?? defaultAtaSettings),
        ataEnabled: action.payload,
      };
      return {
        ...state,
        ataSettings,
      };
    }

    case actionTypes.SET_CUSTOM_GOAL_TEMPO: {
      const ataSettings = {
        ...(state?.ataSettings ?? defaultAtaSettings),
        goalTempo: action.payload,
      };
      return {
        ...state,
        ataSettings,
      };
    }

    case actionTypes.SET_USING_CUSTOM_GOAL_TEMPO: {
      const ataSettings = {
        ...(state?.ataSettings ?? defaultAtaSettings),
        usingCustomGoalTempo: action.payload,
      };
      return {
        ...state,
        ataSettings,
      };
    }

    default:
      // console.log("mainAppReducer: unhandled action type:", action.type);
      return state;
  }
};
